import { forwardRef } from 'react'
import { clsx } from 'clsx'
import type { InputProps } from '@/types'
import styles from './Input.module.css'

interface ExtendedInputProps extends InputProps {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  datalist?: string[]
  // Hint per la tastiera mobile (numeric / tel / email / decimal / ...).
  // Lo accettiamo esplicitamente perche' alcuni campi (es. telefono) lo
  // impostano per forzare la tastiera numerica sui dispositivi touch.
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
}

export const Input = forwardRef<HTMLInputElement, ExtendedInputProps>(({
  label,
  name,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  error,
  hint,
  maxLength,
  pattern,
  autoComplete,
  icon,
  uppercase = false,
  className,
  value,
  onChange,
  onBlur,
  datalist,
  ...rest
}, ref) => {
  const inputId = `input-${name}`
  const datalistId = datalist ? `${inputId}-datalist` : undefined
  
  const wrapperClasses = clsx(
    styles.wrapper,
    {
      [styles.hasError]: error,
      [styles.hasIcon]: icon,
      [styles.disabled]: disabled
    },
    className
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (uppercase) {
      e.target.value = e.target.value.toUpperCase()
    }
    onChange?.(e)
  }

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      
      <div className={styles.inputWrapper}>
        {icon && (
          <span className={styles.iconWrapper}>
            <i className={`bi bi-${icon}`} />
          </span>
        )}
        
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          pattern={pattern}
          autoComplete={datalist ? 'off' : autoComplete}
          list={datalistId}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className={clsx(styles.input, { [styles.uppercase]: uppercase })}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          {...rest}
        />
        
        {datalist && (
          <datalist id={datalistId}>
            {datalist.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        )}
      </div>
      
      {error && (
        <p id={`${inputId}-error`} className={styles.error} role="alert">
          <i className="bi bi-exclamation-circle" />
          {error}
        </p>
      )}
      
      {hint && !error && (
        <p id={`${inputId}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'




